import { toast } from "react-toastify";
import {
  defaultDimensions,
  mergeCustomOutputToAPIFiltersFormatWithComparisonPercentage,
  getComparisonPercentageFiltersRuleTree,
  ruleItemInjector,
} from "../../../helper/Quadrant/quadrantHelper";
import { numberFormatter } from "../../../helper/Reports/common";
import axios from "axios";
import ExcelJS from "exceljs";
import { TIME_LEVEL_DIMENSIONS } from "../../../pages/PerformanceAnalysis/performanceAnalysisConfigs";

function getVisibleColumnKeys(allColumns, currentViewType) {
  return allColumns
    .filter((col) => col?.viewType?.includes(currentViewType))
    .map((col) => col.key);
}

async function requestBackendProductReportExport({
  clientId,
  userPlatform,
  totalCount,
  triggeredBy,
  queryPayload,
  visibleColumnKeys,
  isComparison,
}) {
  const exportUrl = `${process.env.REACT_APP_ADS_API}/client/${clientId}/reports:export?source=${userPlatform}`;
  const exportPayload = {
    report_type: "PRODUCT_ANALYTICS",
    total_count: totalCount,
    triggered_by: triggeredBy,
    query: {
      body: {
        query: queryPayload,
      },
    },
    frontend_config: {
      is_comparison: isComparison,
      columns: visibleColumnKeys,
    },
  };

  await axios.post(exportUrl, exportPayload, {
    headers: { "Content-Type": "application/json" },
  });
}

export async function handleDownloadAllProductData(
  moduleFilters,
  comparisonApi,
  fetchedCustomDate = null,
  userLimit = 5000,
  setIsDownloading = () => {},
  userPlatform,
  clientId,
  clientAccountId,
  cubeQueryDimensions,
  cubeQueryMeasure,
  cubeQueryOrder,
  cubeQueryTimeDimensions,
  isPageUnderDynamicSection,
  customDate,
  excludeSBCampaignsFiltering,
  PlatformWiseKeyMapper,
  transformToTopXArray,
  getComparisonPercentageFiltersRuleTree,
  getComparisonFormatTimeDimension,
  allColumns,
  currentViewType,
  customEndPoints = {},
  downloadOptions = {}
) {
  try {
    setIsDownloading(true);
    toast.info("Preparing report...");

    // === Step 1: Prepare filters ===
    const filters = mergeCustomOutputToAPIFiltersFormatWithComparisonPercentage(
      moduleFilters?.metrics?.custom_output,
      moduleFilters?.dimension?.custom_output,
      moduleFilters?.campaigns?.custom_output,
      moduleFilters?.topX?.custom_output,
      comparisonApi
        ? moduleFilters?.comparisonPercentage?.custom_output
        : null
    );

    let tempFilter = JSON.parse(JSON.stringify(filters));
    if (userPlatform === "amazon") {
      tempFilter = ruleItemInjector(
        tempFilter,
        "dimensions",
        defaultDimensions(clientAccountId)
      );
      tempFilter = ruleItemInjector(
        tempFilter,
        "campaigns",
        defaultDimensions("", [excludeSBCampaignsFiltering])
      );
    }

    // === Step 2: Build URLs ===
    const base = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products`;
    const baseUrl = customEndPoints?.baseUrl || base;
    const url = comparisonApi
      ? `${baseUrl}:comparison?source=${userPlatform}`
      : `${baseUrl}:insights?source=${userPlatform}`;
    const summaryUrl = comparisonApi
      ? `${baseUrl}:comparison-summary?source=${userPlatform}`
      : `${baseUrl}:insights-summary?source=${userPlatform}`;

    // === Step 3: Prepare base payload ===
    const basePayload = {
      query: {
        dimensions: cubeQueryDimensions,
        filters: tempFilter,
        extra_filters: {
          type: `${userPlatform}`,
          [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]:
            tempFilter?.campaigns,
        },
        percentile_filters: transformToTopXArray(tempFilter?.topX) ?? [],
        ...(comparisonApi
          ? (() => {
              const comparisonPercentageFilters =
                getComparisonPercentageFiltersRuleTree(
                  tempFilter?.comparisonPercentage
                );
              return comparisonPercentageFilters
                ? { comparison_percentage_filters: comparisonPercentageFilters }
                : {};
            })()
          : {}),
        measures: cubeQueryMeasure,
        order: cubeQueryOrder,
        date_preset:
          fetchedCustomDate ?? (isPageUnderDynamicSection ? customDate : null),
        timeDimensions: comparisonApi
          ? getComparisonFormatTimeDimension(cubeQueryTimeDimensions)
          : cubeQueryTimeDimensions,
        limit: 1000,
      },
    };

    // === Step 4: Fetch summary once ===
    const summaryDataToPost = {
      query: {
        ...basePayload.query,
        measures: basePayload.query.measures.filter((measure) => !TIME_LEVEL_DIMENSIONS.includes(measure)),
      },
    };
    const summaryRes = await axios.post(summaryUrl, summaryDataToPost);
    const summaryData = summaryRes?.data ?? {};
    const totalCount = summaryData?.total_count ?? 0;

    if (downloadOptions?.enableBackendExport) {
      const visibleColumnKeys =
        downloadOptions?.visibleColumnKeys?.length
          ? downloadOptions.visibleColumnKeys
          : getVisibleColumnKeys(allColumns, currentViewType);
      const exportQueryPayload = {
        ...basePayload.query,
        measures: basePayload.query.measures.filter(
          (measure) => !TIME_LEVEL_DIMENSIONS.includes(measure)
        ),
        offset: 0,
        limit: 50,
      };

      await requestBackendProductReportExport({
        clientId,
        userPlatform,
        totalCount,
        triggeredBy: downloadOptions?.triggeredBy ?? "",
        queryPayload: exportQueryPayload,
        visibleColumnKeys,
        isComparison: comparisonApi,
      });

      downloadOptions?.onBackendExportSuccess?.();
      return;
    }

    // === Step 5: Calculate batch count ===
    const totalBatches = Math.ceil(userLimit / 1000);
    const batchPromises = [];

    for (let i = 0; i < totalBatches; i++) {
      const payload = JSON.parse(JSON.stringify(basePayload));
      payload.query.offset = i * 1000;
      batchPromises.push(axios.post(url, payload));
    }

    // === Step 6: Fetch batches concurrently (safe limit of 5 at once) ===
    const allData = [];
    const concurrencyLimit = 5;
    for (let i = 0; i < batchPromises.length; i += concurrencyLimit) {
      const chunk = batchPromises.slice(i, i + concurrencyLimit);
      const results = await Promise.allSettled(chunk);
      results.forEach((res) => {
        if (res.status === "fulfilled") {
          const dataChunk = res.value?.data?.data ?? [];
          allData.push(...dataChunk);
        }
      });
    }

    if (!allData.length) {
      toast.warn("No data available for download!");
      return;
    }

    // === Step 7: Prepare Excel columns ===
    const visibleColumns = downloadOptions?.visibleColumnKeys?.length
      ? downloadOptions.visibleColumnKeys
          .map((key) => allColumns.find((col) => col.key === key))
          .filter(Boolean)
      : allColumns.filter((col) => col?.viewType?.includes(currentViewType));
    let headers = [];

    visibleColumns.forEach((col) => {
      const base = col.shortTitle || col.title || col.key;

      if (comparisonApi && col.colSpan === 2) {
        headers.push(`${base} (current)`);
        headers.push(`${base} (previous)`);
        headers.push(`${base} (% change)`);
      } else {
        headers.push(base);
      }
    });

    // === Step 8: Format data rows ===
    const rows = allData.map((row) => {
      const obj = {};

      let headerIndex = 0;
      visibleColumns.forEach((col) => {
        // =========================
        // 1. PRODUCT NAME WITH ASIN
        // =========================
        if (
          [
            "VariantAnalysisTablePPMPlaceholder.asin",
            "product_id",
          ].includes(col.key)
        ) {
          const header = headers[headerIndex++];
          const { productName, asin } = col?.innerKeys || {};
          const asinKey = asin?.value;
          const asinLabel = asin?.label || "ASIN";

          const productNameValue =
            userPlatform === "amazon"
              ? row[productName] || ""
              : row[productName] || "";

          const asinValue = row[asinKey] || "";

          obj[header] = `${productNameValue} (${asinLabel}: ${asinValue})`;
          return;
        }

        // ======================================
        // 2. COMPARISON VIEW LOGIC (colSpan === 2)
        // ======================================
        if (comparisonApi && col.colSpan === 2) {
          const base = col.shortTitle || col.title || col.key;

          const currentHeader = `${base} (current)`;
          const previousHeader = `${base} (previous)`;
          const changeHeader = `${base} (% change)`;

          const currentValue = row.current_data?.[col.key];
          obj[currentHeader] =
            currentValue !== undefined && currentValue !== null
              ? numberFormatter(
                  currentValue,
                  !!col.isCurrencyFormat,
                  col?.format,
                  col?.maximumFractionDigits
                )
              : "-";

          const previousValue = row.comparison_data?.[col.key];
          obj[previousHeader] =
            previousValue !== undefined && previousValue !== null
              ? numberFormatter(
                  previousValue,
                  !!col.isCurrencyFormat,
                  col?.format,
                  col?.maximumFractionDigits
                )
              : "-";

          const changeValue = Number(row.change_percentage_data?.[col.key]);
          obj[changeHeader] = !isNaN(changeValue)
            ? `${changeValue?.toFixed(2)}%`
            : "-";
          headerIndex += 3;
          return;
        }

        // ======================================
        // 3. NORMAL VIEW (colSpan = 1 or non-comparison)
        // ======================================
        const header = headers[headerIndex++];
        const rawValue = row[col.key];

        const formattedValue =
          rawValue !== undefined && rawValue !== null && rawValue !== ""
            ? numberFormatter(
                rawValue,
                !!col.isCurrencyFormat,
                col?.format,
                col?.maximumFractionDigits
              )
            : "-";

        obj[header] = formattedValue;
      });

      return obj;
    });

    // === Step 9: Add summary row ===
    const summaryRow = {};
    let headerIndex2 = 0;

    visibleColumns.forEach((col) => {
      // ===========================
      // 1. COMPARISON VIEW SUMMARY
      // ===========================
      if (comparisonApi && col.colSpan === 2) {
        const base = col.shortTitle || col.title || col.key;

        const currentHeader = `${base} (current)`;
        const previousHeader = `${base} (previous)`;
        const changeHeader = `${base} (% change)`;

        const curr = summaryData?.current_data?.[col.key];
        summaryRow[currentHeader] =
          curr !== undefined && curr !== null
            ? numberFormatter(
                curr,
                !!col.isCurrencyFormat,
                col?.format,
                col?.maximumFractionDigits
              )
            : "-";

        const prev = summaryData?.comparison_data?.[col.key];
        summaryRow[previousHeader] =
          prev !== undefined && prev !== null
            ? numberFormatter(
                prev,
                !!col.isCurrencyFormat,
                col?.format,
                col?.maximumFractionDigits
              )
            : "-";

        const rawChange = summaryData?.change_percentage_data?.[col.key];
        const num = parseFloat(rawChange);

        summaryRow[changeHeader] = !isNaN(num) ? `${num?.toFixed(2)}%` : "-";

        headerIndex2 += 3;
        return;
      }

      // ===========================
      // 2. NORMAL VIEW SUMMARY
      // ===========================
      const header = headers[headerIndex2++];
      const rawValue = summaryData?.[col.key];

      summaryRow[header] =
        rawValue !== undefined && rawValue !== null
          ? numberFormatter(
              rawValue,
              !!col.isCurrencyFormat,
              col?.format,
              col?.maximumFractionDigits
            )
          : "-";
    });

    rows.unshift({ ...summaryRow, [headers[0]]: "Summary" });

    // === Step 10: Create and download Excel ===
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Full Product Report");

    worksheet.columns = headers.map((header) => ({
      header,
      key: header,
      width: 22,
    }));

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    worksheet.addRows(rows);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `Product_Report_${allData.length}_Records_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);

    toast.success(`Excel downloaded successfully (${allData.length} records)!`);
  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message ?? "Failed to download full data!"
    );
  } finally {
    setIsDownloading(false);
  }
}
